export const DEEP_LINK_STORAGE_KEY = 'tenantDeepLink';

/** @deprecated Use DEEP_LINK_STORAGE_KEY — kept for backward compatibility. */
export const LEGACY_LEASE_UPDATE_DEEP_LINK_KEY = 'leaseUpdateDeepLink';

export type DeepLinkAction =
  | 'update_lease_dates'
  | 'add_payment_method'
  | 'add_property'
  | 'payment'
  | 'autopay';

export interface DeepLinkIntent {
  action: DeepLinkAction;
  tenantBuildingId?: string;
}

interface DeepLinkActionConfig {
  webRoute: string;
  mobileAppPath: string;
}

const DEEP_LINK_ACTION_CONFIG: Record<DeepLinkAction, DeepLinkActionConfig> = {
  update_lease_dates: {
    webRoute: '/dashboard',
    mobileAppPath: 'update-lease-dates',
  },
  add_payment_method: {
    webRoute: '/payment-methods',
    mobileAppPath: 'add-payment-method',
  },
  add_property: {
    webRoute: '/add-property',
    mobileAppPath: 'add-property',
  },
  payment: {
    webRoute: '/pay-rent',
    mobileAppPath: 'payment',
  },
  autopay: {
    webRoute: '/pay-rent',
    mobileAppPath: 'autopay',
  },
};

const MOBILE_APP_SCHEME = 'tenantpay';
const ANDROID_APP_PACKAGE = 'com.tenantpay.android';

const PATH_ACTION_ALIASES: Record<string, DeepLinkAction> = Object.fromEntries(
  (Object.entries(DEEP_LINK_ACTION_CONFIG) as [DeepLinkAction, DeepLinkActionConfig][]).map(
    ([action, config]) => [config.mobileAppPath, action]
  )
);

const KNOWN_DEEP_LINK_ACTIONS = new Set<string>(Object.keys(DEEP_LINK_ACTION_CONFIG));

export const isMobileDevice = (): boolean =>
  /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const isDeepLinkAction = (value: string): value is DeepLinkAction =>
  KNOWN_DEEP_LINK_ACTIONS.has(value);

export const normalizeDeepLinkAction = (value: string): DeepLinkAction | null => {
  if (isDeepLinkAction(value)) {
    return value;
  }

  return PATH_ACTION_ALIASES[value] ?? null;
};

export const buildDeepLinkQueryUrl = (
  tenantPortalUrl: string,
  intent: DeepLinkIntent
): string => {
  const url = new URL(tenantPortalUrl);
  url.searchParams.set('action', intent.action);

  if (intent.tenantBuildingId) {
    url.searchParams.set('tenant_building_id', intent.tenantBuildingId);
  }

  return url.toString();
};

export const buildDeepLinkGoUrl = (
  tenantPortalUrl: string,
  intent: DeepLinkIntent
): string => {
  const config = DEEP_LINK_ACTION_CONFIG[intent.action];
  const url = new URL(`${tenantPortalUrl.replace(/\/$/, '')}/go/${config.mobileAppPath}`);

  if (intent.tenantBuildingId) {
    url.searchParams.set('tenant_building_id', intent.tenantBuildingId);
  }

  return url.toString();
};

export const getWebRouteForDeepLink = (intent: DeepLinkIntent): string =>
  DEEP_LINK_ACTION_CONFIG[intent.action].webRoute;

export const storeDeepLinkIntent = (intent: DeepLinkIntent): void => {
  sessionStorage.setItem(DEEP_LINK_STORAGE_KEY, JSON.stringify(intent));
};

export const readDeepLinkIntent = (): DeepLinkIntent | null => {
  const raw =
    sessionStorage.getItem(DEEP_LINK_STORAGE_KEY) ??
    sessionStorage.getItem(LEGACY_LEASE_UPDATE_DEEP_LINK_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as {
      action?: string;
      tenantBuildingId?: string;
    };

    if (parsed.action && isDeepLinkAction(parsed.action)) {
      return {
        action: parsed.action,
        tenantBuildingId: parsed.tenantBuildingId,
      };
    }

    if (parsed.tenantBuildingId) {
      return {
        action: 'update_lease_dates',
        tenantBuildingId: parsed.tenantBuildingId,
      };
    }
  } catch {
    return null;
  }

  return null;
};

export const consumeDeepLinkIntent = (): DeepLinkIntent | null => {
  const intent = readDeepLinkIntent();
  clearDeepLinkIntent();
  return intent;
};

export const clearDeepLinkIntent = (): void => {
  sessionStorage.removeItem(DEEP_LINK_STORAGE_KEY);
  sessionStorage.removeItem(LEGACY_LEASE_UPDATE_DEEP_LINK_KEY);
};

const parseDeepLinkIntent = (params: {
  action: string | null;
  tenantBuildingId?: string | null;
}): DeepLinkIntent | null => {
  if (!params.action) {
    return null;
  }

  const action = normalizeDeepLinkAction(params.action);
  if (!action) {
    return null;
  }

  return {
    action,
    tenantBuildingId: params.tenantBuildingId ?? undefined,
  };
};

export const parseDeepLinkFromSearch = (search: string): DeepLinkIntent | null => {
  const searchParams = new URLSearchParams(search);
  return parseDeepLinkIntent({
    action: searchParams.get('action'),
    tenantBuildingId: searchParams.get('tenant_building_id'),
  });
};

export const parseDeepLinkFromPath = (pathname: string, search: string): DeepLinkIntent | null => {
  const goMatch = pathname.match(/^\/go\/([^/]+)\/?$/);
  if (goMatch) {
    const searchParams = new URLSearchParams(search);
    return parseDeepLinkIntent({
      action: goMatch[1],
      tenantBuildingId: searchParams.get('tenant_building_id'),
    });
  }

  const editPropertyMatch = pathname.match(/^\/edit-property\/([^/]+)\/?$/);
  if (editPropertyMatch) {
    return {
      action: 'update_lease_dates',
      tenantBuildingId: editPropertyMatch[1],
    };
  }

  return null;
};

const buildMobileAppUrl = (intent: DeepLinkIntent): string => {
  const config = DEEP_LINK_ACTION_CONFIG[intent.action];
  const query = intent.tenantBuildingId
    ? `?tenant_building_id=${encodeURIComponent(intent.tenantBuildingId)}`
    : '';

  return `${MOBILE_APP_SCHEME}://${config.mobileAppPath}${query}`;
};

export const tryOpenMobileApp = (intent: DeepLinkIntent): void => {
  if (!isMobileDevice()) {
    return;
  }

  const appUrl = buildMobileAppUrl(intent);
  const userAgent = navigator.userAgent;

  if (/Android/i.test(userAgent)) {
    const webFallbackUrl = encodeURIComponent(window.location.href);
    window.location.href = `intent://${appUrl.replace(`${MOBILE_APP_SCHEME}://`, '')}#Intent;scheme=${MOBILE_APP_SCHEME};package=${ANDROID_APP_PACKAGE};S.browser_fallback_url=${webFallbackUrl};end`;
    return;
  }

  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    window.location.href = appUrl;
  }
};

export const captureDeepLinkFromLocation = (): DeepLinkIntent | null => {
  const pathname = window.location.pathname;
  const search = window.location.search;

  const intent =
    parseDeepLinkFromSearch(search) ?? parseDeepLinkFromPath(pathname, search);

  if (!intent) {
    return null;
  }

  storeDeepLinkIntent(intent);
  tryOpenMobileApp(intent);

  const url = new URL(window.location.href);
  url.pathname = '/';
  url.searchParams.delete('action');
  url.searchParams.delete('tenant_building_id');
  window.history.replaceState(null, '', url.toString());

  return intent;
};

export const getPostLoginRouteForDeepLink = (): string | null => {
  const intent = readDeepLinkIntent();
  if (!intent) {
    return null;
  }

  return getWebRouteForDeepLink(intent);
};
